import React, { useState, useRef } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { ScanLine, Upload, FileText, Plus, Loader2 } from 'lucide-react';

export default function InvoiceScanner({ projectId, onItemsScanned }) {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleScan = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanning(true);
    setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('project_id', projectId || '');
    try {
      const res = await api.scanInvoice(fd);
      setResult(res.data);
      toast.success(`Faktura skannad: ${res.data.items?.length || 0} artiklar hittade`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Skanning misslyckades');
    } finally {
      setScanning(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAddToMaterials = () => {
    if (result?.items && onItemsScanned) {
      onItemsScanned(result.items.map(item => ({
        name: item.name,
        specific_product: item.name,
        quantity: item.quantity || 1,
        unit: item.unit || 'st',
        estimated_unit_price: item.unit_price || 0,
        estimated_total_price: item.total_price || 0,
        where_to_buy: result.supplier_name || '',
        article_number: item.article_number || '',
      })));
      toast.success('Artiklar tillagda i materialplan');
      setResult(null);
    }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5 space-y-4" data-testid="invoice-scanner">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold flex items-center gap-2">
            <ScanLine size={16} className="text-[#E8613A]" /> Skanna leverantörsfaktura
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">Ladda upp PDF eller bild — AI läser av artiklar och priser</p>
        </div>
        <div>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" onChange={handleScan} className="hidden" data-testid="scan-file-input" />
          <Button size="sm" onClick={() => fileRef.current?.click()} disabled={scanning} className="bg-[#E8613A] hover:bg-[#d4552f] text-white" data-testid="scan-btn">
            {scanning ? <><Loader2 size={14} className="mr-1 animate-spin" /> Skannar...</> : <><Upload size={14} className="mr-1" /> Ladda upp faktura</>}
          </Button>
        </div>
      </div>

      {result && (
        <div className="space-y-3">
          <div className="bg-muted/50 rounded-lg p-3 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">{result.supplier_name || 'Okänd leverantör'}</p>
              <p className="text-xs text-muted-foreground">Faktura {result.invoice_number || '—'} • {result.invoice_date || '—'}</p>
            </div>
            <p className="text-base font-bold">{result.total_amount?.toLocaleString('sv-SE')} kr</p>
          </div>

          <table className="w-full text-sm">
            <thead className="bg-muted/30">
              <tr>
                <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">Artikel</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Antal</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Pris</th>
                <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(result.items || []).map((item, i) => (
                <tr key={i}>
                  <td className="px-3 py-1.5">{item.name}</td>
                  <td className="px-3 py-1.5 text-right">{item.quantity} {item.unit}</td>
                  <td className="px-3 py-1.5 text-right">{item.unit_price?.toLocaleString('sv-SE')} kr</td>
                  <td className="px-3 py-1.5 text-right font-medium">{item.total_price?.toLocaleString('sv-SE')} kr</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Button size="sm" onClick={handleAddToMaterials} className="w-full" data-testid="add-scanned-to-materials-btn">
            <Plus size={14} className="mr-1" /> Lägg till {result.items?.length || 0} artiklar i materialplan
          </Button>
        </div>
      )}
    </div>
  );
}
